import { useState, useEffect, useCallback } from "react";
import { adminApi, ApiError } from "../lib/api";
import {
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
} from "lucide-react";
import { cn } from "../lib/utils";

interface CronJob {
  job: string;
  lastRun: {
    id: number;
    status: string;
    triggeredBy: string;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    result: unknown;
    errorMessage: string | null;
  } | null;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const JOB_LABELS: Record<string, string> = {
  daily_health: "Health Score Recalculation",
  hard_delete: "Hard Delete Cleanup",
  notifications_hourly: "Send Notifications",
};

export default function Services() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [result, setResult] = useState<{
    job: string;
    type: "success" | "error";
    message: string;
  } | null>(null);

  const fetchStatus = useCallback(() => {
    adminApi
      .cronStatus()
      .then((res) => setJobs(res.jobs))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleTrigger = async (jobName: string) => {
    setRunningJob(jobName);
    setResult(null);

    try {
      const res = await adminApi.cronTrigger(jobName);
      setResult({
        job: jobName,
        type: "success",
        message:
          res.status === "completed"
            ? "Completed successfully"
            : res.message || "Job started",
      });
      fetchStatus();
    } catch (err) {
      setResult({
        job: jobName,
        type: "error",
        message:
          err instanceof ApiError ? err.message : "Failed to trigger job",
      });
    } finally {
      setRunningJob(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Services</h1>
        <button
          onClick={fetchStatus}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={32} className="animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => {
            const isRunning = runningJob === job.job;
            const jobResult = result?.job === job.job ? result : null;
            const label = JOB_LABELS[job.job] || job.job;

            return (
              <div
                key={job.job}
                className="bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900">{label}</h3>
                    <p className="text-xs font-mono text-slate-400 mt-0.5">
                      {job.job}
                    </p>

                    {job.lastRun ? (
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                        <div className="flex items-center gap-1.5">
                          {job.lastRun.status === "success" ? (
                            <CheckCircle
                              size={14}
                              className="text-emerald-500"
                            />
                          ) : job.lastRun.status === "running" ? (
                            <Loader2
                              size={14}
                              className="text-blue-500 animate-spin"
                            />
                          ) : (
                            <XCircle size={14} className="text-red-500" />
                          )}
                          <span
                            className={cn(
                              "font-semibold uppercase",
                              job.lastRun.status === "success"
                                ? "text-emerald-600"
                                : job.lastRun.status === "running"
                                  ? "text-blue-600"
                                  : "text-red-600",
                            )}
                          >
                            {job.lastRun.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Clock size={12} />
                          {formatTime(job.lastRun.startedAt)}
                        </div>
                        <span className="text-slate-400">
                          {formatDuration(job.lastRun.durationMs)}
                        </span>
                        <span className="text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
                          {job.lastRun.triggeredBy}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 mt-2">Never run</p>
                    )}

                    {job.lastRun?.errorMessage && (
                      <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-3 font-mono">
                        {job.lastRun.errorMessage}
                      </p>
                    )}

                    {jobResult && (
                      <div
                        className={cn(
                          "flex items-center gap-2 text-xs font-medium mt-3 px-3 py-2 rounded-lg",
                          jobResult.type === "success"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700",
                        )}
                      >
                        {jobResult.type === "success" ? (
                          <CheckCircle size={14} />
                        ) : (
                          <XCircle size={14} />
                        )}
                        {jobResult.message}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleTrigger(job.job)}
                    disabled={isRunning}
                    className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                  >
                    {isRunning ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Play size={16} />
                    )}
                    {isRunning ? "Running..." : "Run Now"}
                  </button>
                </div>
              </div>
            );
          })}

          {jobs.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm">No cron jobs found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
